import logEvent from 'analytics/logEvent';
import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import useValidation from 'hooks/useValidation';
import Cookies from 'js-cookie';
import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SchemaOf } from 'yup';
import * as yup from 'yup';

import pagesUrls from 'constants/pagesUrls';

import ALink from 'components/basic/ALink';
import Button from 'components/basic/Button';
import Checkbox from 'components/basic/Checkbox';
import ErrorLabel from 'components/basic/ErrorLabel';
import InputText from 'components/basic/InputText';
import { BodyM, Caption } from 'components/basic/Typography';
import SocialLogins from 'components/complex/SocialLogins';

import type { AppState } from 'redux-store';
import { useAppDispatch, useAppSelector } from 'redux-store';
import type { RegistrationRequestType } from 'redux-store/slices/profile/types';
import registrationThunk from 'redux-store/thunks/profile/registrationThunk';

import s from './RegistrationForm.module.scss';

export default function RegistrationForm({
  redirectUrl = '',
}: {
  redirectUrl?: string;
}): JSX.Element {
  const registrationError = useAppSelector<AppState['profile']['error']>(
    appState => appState.profile.error,
  );
  const providers = useAppSelector(state => state.profile.providers);
  const t = useTranslationAndTypograph();
  const schema = useMemo((): SchemaOf<RegistrationRequestType> => {
    return yup.object().shape({
      username: yup.string().min(1).required('Please enter your name'),
      email: yup.string().min(1).required('Please enter your email'),
      password: yup.string().min(6).required('Please enter your password'),
    });
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isEmailOptInChecked, emailOptInChecked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { validationErrors, validateAllFields, doValidate } =
    useValidation<RegistrationRequestType>(
      { email, password, username },
      schema,
    );
  const dispatch = useAppDispatch();

  useEffect(() => {
    Cookies.set('next-auth-email-opt-in', JSON.stringify(isEmailOptInChecked));
  }, [isEmailOptInChecked]);

  function buttonClickLogEvent(method: string): void {
    logEvent('Web_Auth_Sign-up_Button_Clicked', {
      method: method,
      'e-mail_opt-in': isEmailOptInChecked,
    });
  }

  async function doRegistration(e: FormEvent): Promise<void> {
    e.preventDefault();

    buttonClickLogEvent('email');

    const isValidationPassed = await validateAllFields();

    if (isValidationPassed) {
      setIsLoading(true);

      Cookies.set('next-auth-redirect-url', redirectUrl);

      await dispatch(
        registrationThunk({
          email,
          password,
          username,
        }),
      );
      setIsLoading(false);
    }
  }

  return (
    <>
      <form onSubmit={doRegistration}>
        <InputText<RegistrationRequestType>
          labelText={t('Your Name')}
          value={username}
          name="username"
          onChange={setUsername}
          doValidate={doValidate}
          errorMessage={validationErrors.username}
          className="mb-5 mt-5 cy-registration-name-input"
        />
        <InputText<RegistrationRequestType>
          labelText={t('Email')}
          value={email}
          name="email"
          onChange={setEmail}
          doValidate={doValidate}
          errorMessage={validationErrors.email}
          autocomplete="off"
          className="mb-5 mt-5 cy-registration-email-input"
        />
        <InputText<RegistrationRequestType>
          labelText={t('Password')}
          value={password}
          name="password"
          type="password"
          onChange={setPassword}
          doValidate={doValidate}
          errorMessage={validationErrors.password}
          autocomplete="off"
          className="mb-5 mt-5 cy-registration-password-input"
        />
        {registrationError && (
          <ErrorLabel className="mb-8">{t(registrationError)}</ErrorLabel>
        )}
        <Checkbox
          id="2"
          name="2"
          className={cn(s.checkbox, 'cy-email-opt-in')}
          labelText={
            <Caption className={s.checkboxText}>
              {t(
                'We may send you email newsletters on your health, our new features, studies, and special offers. You can unsubscribe now or anytime later.',
              )}
            </Caption>
          }
          checked={isEmailOptInChecked}
          onChange={emailOptInChecked}
        />
        <Button
          type="submit"
          disabled={isLoading}
          isLoading={isLoading}
          className={cn(s.button, 'mb-8 cy-registration-button')}
          size="medium"
        >
          {t('sign up')}
        </Button>
      </form>
      <BodyM className="mb-4">{t('or continue with')}</BodyM>
      <SocialLogins
        providers={providers}
        redirectUrl={redirectUrl}
        buttonClickLogEvent={buttonClickLogEvent}
      />
      <Caption className={cn('mt-8 cy-boring-links-form', s.boringLinks)}>
        {t('By signing up, you accept our')}{' '}
        <ALink
          href={pagesUrls.terms}
          onClick={(): void => {
            logEvent('Web_Auth_Sign-up_Terms_Link_Clicked');
          }}
          className="cy-term-of-use-form-link"
          target="_blank"
        >
          {t('With terms')}
        </ALink>{' '}
        {t('And')}{' '}
        <ALink
          href={pagesUrls.privacy}
          className="cy-privacy-form-link"
          onClick={(): void => {
            logEvent('Web_Auth_Sign-up_Privacy_Link_Clicked');
          }}
          target="_blank"
        >
          {t('With policy')}
        </ALink>
      </Caption>
    </>
  );
}
